/**
 * TapAddGhost — in-place feedback for AI tap-add (#411).
 *
 * Renders a dashed rect at the user's tap location with a state-aware
 * pill ("🔮 Detecting… 2.1s" → "✓ Bosch drill · 2143ms" → orange
 * "(unrecognized — tap to name)" → red "Try again"). All four states
 * morph in-place rather than swapping out — keeps the spatial
 * anchor stable so the user remembers where they tapped.
 *
 * Auto-dismiss timers:
 *   - success → 1.8s
 *   - softfail → no auto-dismiss (the parent opens the rename sheet)
 *   - harderror → 4.5s
 *
 * The pending state has no auto-dismiss; the parent removes the
 * record on the BE response.
 */

import React, { useEffect, useState } from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../../../styles/theme';
import type { Bbox, ImageTransform } from '../useImageTransform';

export type TapAddStatus = 'pending' | 'success' | 'softfail' | 'harderror';

export interface TapAddRecord {
  /** Local id — used by parent to dedupe + dismiss. */
  id: string;
  /** Image-space bbox; transformed to DOM-space on render. */
  bbox: Bbox;
  /** performance.now() at tap time. */
  startedAt: number;
  status: TapAddStatus;
  /** Set when the BE call completes — both success and error. */
  resolvedAt?: number;
  /** Final BE latency in ms (success only). */
  latencyMs?: number;
  /** AI-derived item name (success); placeholder on softfail. */
  itemName?: string;
  /** Friendly error message for harderror; raw BE detail for softfail. */
  errorMessage?: string;
  /** True after the auto-dismiss timer elapses — parent removes
   *  from the array on the next tick. */
  expired?: boolean;
}

interface Props {
  record: TapAddRecord;
  transform: ImageTransform;
  /** Caller dismisses the ghost from the list when fired (used by
   *  the close-X on hard-error and by the auto-dismiss timer). */
  onDismiss: (id: string) => void;
  /** Retry handler for the harderror state — fires another tap with
   *  the same bbox. */
  onRetry?: (record: TapAddRecord) => void;
}

const AUTO_DISMISS_MS: Partial<Record<TapAddStatus, number>> = {
  success: 1800,
  harderror: 4500,
};

export const TapAddGhost: React.FC<Props> = ({
  record,
  transform,
  onDismiss,
  onRetry,
}) => {
  // 100ms-stepping live counter for the pending pill. Stops the
  // moment the record resolves.
  const [elapsedMs, setElapsedMs] = useState(0);
  useEffect(() => {
    if (record.status !== 'pending') return;
    const tick = () => setElapsedMs(Math.round(performance.now() - record.startedAt));
    tick();
    const id = setInterval(tick, 100);
    return () => clearInterval(id);
  }, [record.status, record.startedAt]);

  // Auto-dismiss on success/harderror.
  useEffect(() => {
    const ttl = AUTO_DISMISS_MS[record.status];
    if (!ttl) return;
    const id = setTimeout(() => onDismiss(record.id), ttl);
    return () => clearTimeout(id);
  }, [record.status, record.id, onDismiss]);

  const rect = transform.ready ? transform.toDomBbox(record.bbox) : null;
  if (!rect) return null;

  const tone = TONE[record.status];

  // Pill copy.
  let label: string;
  let icon: keyof typeof MaterialIcons.glyphMap;
  if (record.status === 'pending') {
    label = `Detecting… ${(elapsedMs / 1000).toFixed(1)}s`;
    icon = 'auto-awesome';
  } else if (record.status === 'success') {
    const ms = record.latencyMs ?? 0;
    label = `✓ ${record.itemName ?? 'Added'} · ${ms}ms`;
    icon = 'check-circle';
  } else if (record.status === 'softfail') {
    label = 'Tap to name';
    icon = 'edit';
  } else {
    label = record.errorMessage || 'Couldn\'t add';
    icon = 'error-outline';
  }

  return (
    <View pointerEvents="box-none" style={StyleSheet.absoluteFill}>
      {/* Ghost rect — same outline + dashed style as the live
          draft for visual continuity, tinted by status. */}
      <View
        pointerEvents="none"
        style={[
          styles.rect,
          {
            left: rect.x,
            top: rect.y,
            width: rect.w,
            height: rect.h,
            borderColor: tone.border,
            backgroundColor: tone.fill,
          },
        ]}
      />
      {/* Pill anchored to the top-right outside of the rect (or top-
          left if the rect hugs the right edge). */}
      <View
        pointerEvents="box-none"
        style={[
          styles.pillWrap,
          {
            left: rect.x + Math.min(rect.w, 12),
            top: Math.max(0, rect.y - 32),
          },
        ]}
      >
        <View style={[styles.pill, { backgroundColor: tone.pillBg }]}>
          <MaterialIcons name={icon} size={12} color={tone.pillFg} />
          <Text style={[styles.pillText, { color: tone.pillFg }]} numberOfLines={1}>
            {label}
          </Text>
          {record.status === 'harderror' ? (
            <>
              {onRetry ? (
                <Pressable
                  onPress={() => onRetry(record)}
                  style={styles.pillAction}
                  accessibilityLabel="Retry"
                  testID={`tap-add-retry-${record.id}`}
                >
                  <MaterialIcons name="refresh" size={12} color={tone.pillFg} />
                </Pressable>
              ) : null}
              <Pressable
                onPress={() => onDismiss(record.id)}
                style={styles.pillAction}
                accessibilityLabel="Dismiss"
                testID={`tap-add-dismiss-${record.id}`}
              >
                <MaterialIcons name="close" size={12} color={tone.pillFg} />
              </Pressable>
            </>
          ) : null}
        </View>
      </View>
    </View>
  );
};

interface Tone {
  border: string;
  fill: string;
  pillBg: string;
  pillFg: string;
}

const TONE: Record<TapAddStatus, Tone> = {
  pending: {
    border: fpColors.primary,
    fill: `${fpColors.primary}22`,
    pillBg: fpColors.primary,
    pillFg: '#ffffff',
  },
  success: {
    border: '#1A8A3A', // green
    fill: '#1A8A3A1f',
    pillBg: '#1A8A3A',
    pillFg: '#ffffff',
  },
  softfail: {
    border: '#B98700', // amber
    fill: '#B987001f',
    pillBg: '#B98700',
    pillFg: '#ffffff',
  },
  harderror: {
    border: fpColors.error ?? '#B00020',
    fill: `${fpColors.error ?? '#B00020'}1f`,
    pillBg: fpColors.error ?? '#B00020',
    pillFg: '#ffffff',
  },
};

const styles = StyleSheet.create({
  rect: {
    position: 'absolute',
    borderWidth: 2,
    borderStyle: 'dashed',
    borderRadius: 4,
    ...(Platform.OS === 'web'
      ? ({
          boxShadow: `0 0 0 2px rgba(255,255,255,0.6)`,
        } as any)
      : null),
  },
  pillWrap: {
    position: 'absolute',
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
    shadowColor: '#000',
    shadowOpacity: 0.2,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 4,
    elevation: 4,
    maxWidth: 240,
  },
  pillText: {
    fontSize: 11,
    fontWeight: '600',
  },
  pillAction: {
    paddingHorizontal: 2,
    paddingVertical: 2,
    marginLeft: 2,
  },
});

export default TapAddGhost;
